// src/pages/admin/news/NewsAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Space,
  Switch,
  Table,
  Input,
  Tag,
  Modal,
  message,
  Upload,
  Progress,
} from "antd";
import { PlusOutlined, EditOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import RichText from "../RichTextEditor/RichTextEditor";
import { uploadOne } from "../../../utils/utils"; // must resolve to a URL and support onProgress

const orderKey = (o) =>
  o == null || Number(o) <= 0 ? Number.POSITIVE_INFINITY : Number(o);

const StatusTag = ({ v }) =>
  v === true ? (
    <Tag color="green">Published</Tag>
  ) : v === false ? (
    <Tag color="red">Declined</Tag>
  ) : (
    <Tag>Pending</Tag>
  );

const rawFile = (f) => (f?.originFileObj ? f.originFileObj : f);

export default function NewsAdmin({ project }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "",
    summary: "",
    contentHtml: "",
    coverImage: "",
    isPublished: false,
    order: null,
  });

  // cover upload state
  const [coverFiles, setCoverFiles] = useState([]); // antd Upload fileList (max 1)
  const [coverPct, setCoverPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ open: false, src: "", title: "" });

  const fetchList = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        routes.capacityAdminNewsList(project._id)
      );
      const list = data?.news || [];
      list.sort(
        (a, b) =>
          orderKey(a.order) - orderKey(b.order) ||
          new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setRows(list);
    } catch (e) {
      message.error("Failed to load news");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project?._id) fetchList();
    // eslint-disable-next-line
  }, [project?._id]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter((n) =>
      [n.title, n.summary]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const setPublish = async (record, publish) => {
    try {
      await axios.patch(routes.capacityAdminNewsPublish(record._id), {
        publish,
      });
      setRows((prev) =>
        prev.map((r) =>
          r._id === record._id
            ? {
                ...r,
                isPublished: publish === "pending" ? undefined : !!publish,
                publishDate:
                  publish === true ? new Date().toISOString() : undefined,
              }
            : r
        )
      );
    } catch {
      message.error("Failed to update publish");
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      title: "",
      summary: "",
      contentHtml: "",
      coverImage: "",
      isPublished: false,
      order: null,
    });
    setCoverFiles([]);
    setCoverPct(0);
    setModalOpen(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    setForm({
      title: rec.title || "",
      summary: rec.summary || "",
      contentHtml: rec.contentHtml || "",
      coverImage: rec.coverImage || "",
      isPublished: !!rec.isPublished,
      order: rec.order ?? null,
    });
    // show existing cover as preview (won't re-upload)
    setCoverFiles(
      rec.coverImage
        ? [
            {
              uid: "cover-1",
              name: rec.coverImage.split("/").pop() || "cover",
              status: "done",
              url: rec.coverImage,
            },
          ]
        : []
    );
    setCoverPct(0);
    setModalOpen(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      setCoverPct(0);

      // Only upload if a new file is selected; keep URL otherwise.
      let coverUrl = (form.coverImage || "").trim();

      if (coverFiles[0]?.originFileObj) {
        // New file selected
        coverUrl = await uploadOne(rawFile(coverFiles[0]), setCoverPct);
        setCoverPct(100);
      } else if (!coverUrl && coverFiles[0]?.url) {
        // Existing preview (from edit) with URL
        coverUrl = coverFiles[0].url;
      }

      const payload = { ...form, coverImage: coverUrl };

      if (editing?._id) {
        await axios.patch(routes.capacityAdminNewsItem(editing._id), payload);
      } else {
        await axios.post(routes.capacityAdminNewsAdd(project._id), payload);
      }

      message.success("Saved");
      setModalOpen(false);
      fetchList();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: "#", width: 64, align: "center", render: (_v, _r, i) => i + 1 },
    { title: "Title", dataIndex: "title" },
    {
      title: "Status",
      width: 140,
      render: (_, r) => <StatusTag v={r.isPublished} />,
    },
    {
      title: "Order",
      width: 80,
      render: (_, r) => (r.order > 0 ? r.order : "—"),
    },
    {
      title: "Actions",
      width: 220,
      render: (_, r) => (
        <Space wrap>
          <Switch
            checked={r.isPublished === true}
            onChange={(c) => setPublish(r, c ? true : false)}
          />
          <Button icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="Search news…"
          allowClear
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onSearch={(v) => setQ(v)}
          style={{ width: 320 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          Add News
        </Button>
      </Space>

      <Table
        rowKey={(r) => r._id}
        dataSource={filtered}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
      />

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        width={800}
        title={editing ? "Edit News" : "Add News"}
        confirmLoading={saving}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ fontWeight: 500 }}>Title</div>
          <Input
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />

          <div style={{ fontWeight: 500 }}>Summary</div>
          <Input.TextArea
            rows={3}
            placeholder="Summary"
            value={form.summary}
            onChange={(e) =>
              setForm((f) => ({ ...f, summary: e.target.value }))
            }
          />

          <div style={{ fontWeight: 500, marginTop: 8 }}>Cover Image</div>
          <Space align="start" wrap style={{ gap: 12 }}>
            <Upload
              listType="picture-card"
              fileList={coverFiles}
              beforeUpload={() => false}
              onChange={({ fileList }) => setCoverFiles(fileList.slice(-1))}
              onPreview={(file) =>
                setPreview({
                  open: true,
                  src: file.url || file.thumbUrl,
                  title: file.name || "Preview",
                })
              }
              maxCount={1}
              accept="image/*"
            >
              {coverFiles.length >= 1 ? null : (
                <div style={{ width: 140 }}>
                  <UploadOutlined /> <div style={{ marginTop: 4 }}>Upload</div>
                </div>
              )}
            </Upload>

            {/* Progress only when saving a new file */}
            {saving && coverFiles[0]?.originFileObj && (
              <div style={{ width: 180, alignSelf: "center" }}>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Cover upload</div>
                <Progress percent={coverPct} size="small" />
              </div>
            )}
          </Space>

          <div>
            <div style={{ fontWeight: 500, marginTop: 8 }}>Content</div>
            <RichText
              value={form.contentHtml}
              onChange={(html) => setForm((f) => ({ ...f, contentHtml: html }))}
            />
          </div>
        </Space>
      </Modal>

      {/* Shared image preview modal */}
      <Modal
        open={preview.open}
        title={preview.title}
        footer={null}
        onCancel={() => setPreview((p) => ({ ...p, open: false }))}
      >
        <img alt="preview" style={{ width: "100%" }} src={preview.src} />
      </Modal>
    </>
  );
}
